import React from "react";
import { Box, Typography, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import {
  surface, surfaceRaised, border, textPrimary, textMuted,
  accent, font, shadowMed, easing, success, warning, error as errorColor,
} from "../layouts/schoolPortal/schoolPortalTheme";

// Situation types that have a concrete student to navigate to. Cluster/
// instructor situations are informational — there's no single "open this"
// target, so they render without an action.
const NAVIGABLE_TYPES = new Set(["went_dark", "newly_at_risk", "subject_mastered"]);

const severityColor = (severity) => {
  if (severity >= 70) return errorColor;
  if (severity >= 50) return warning;
  return "#2563eb";
};

function SituationRow({ situation, onAcknowledge, navigate, dimmed }) {
  const isPositive = situation.sentiment === "positive";
  const color = isPositive ? success : severityColor(situation.severity);
  const navigable = NAVIGABLE_TYPES.has(situation.type) && situation.facts?.studentId;

  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.25, py: 1.1, px: 1.25, borderRadius: "10px",
      backgroundColor: dimmed ? "transparent" : surfaceRaised,
      opacity: dimmed ? 0.7 : 1,
      transition: `background-color 0.15s ${easing}`,
    }}>
      {isPositive ? (
        <CheckCircleIcon sx={{ fontSize: 15, color, flexShrink: 0 }} />
      ) : (
        <Box sx={{ width: 8, height: 8, borderRadius: "999px", backgroundColor: color, flexShrink: 0 }} />
      )}
      <Box
        onClick={navigable ? () => navigate(`/school/students/${situation.facts.studentId}`) : undefined}
        sx={{ flex: 1, minWidth: 0, cursor: navigable ? "pointer" : "default" }}
      >
        <Typography sx={{ fontSize: "13px", color: textPrimary, fontFamily: font, lineHeight: 1.5 }}>
          {situation.headline}
        </Typography>
      </Box>
      {navigable && (
        <ChevronRightIcon
          onClick={() => navigate(`/school/students/${situation.facts.studentId}`)}
          sx={{ fontSize: 18, color: textMuted, cursor: "pointer", flexShrink: 0 }}
        />
      )}
      {!dimmed && (
        <Button
          size="small"
          onClick={() => onAcknowledge(situation)}
          sx={{
            flexShrink: 0, color: textMuted, textTransform: "none", fontSize: "11px", fontWeight: 600,
            fontFamily: font, minWidth: 0, p: 0.5, "&:hover": { color: accent },
          }}
        >
          Got it
        </Button>
      )}
    </Box>
  );
}

// Data-driven, not self-fetching — `board` arrives as part of the parent
// dashboard's single initial load (same Promise.all as the stat tiles), so
// this never shows its own separate loading spinner. By the time the page
// renders at all, this is already populated.
export default function SchoolBoard({ board, onAcknowledge }) {
  const navigate = useNavigate();

  const situations = board?.situations || [];
  const ongoing = board?.ongoing || [];
  const negatives = situations.filter((s) => s.sentiment !== "positive");
  const positives = situations.filter((s) => s.sentiment === "positive");
  const hasNegatives = negatives.length > 0;
  const hasPositives = positives.length > 0;
  const hasOngoing = ongoing.length > 0;

  return (
    <Box sx={{ backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "16px", boxShadow: shadowMed, p: { xs: 2.5, sm: 3 }, mb: 2.5 }}>
      {hasNegatives ? (
        <>
          <Typography sx={{ fontSize: "15px", fontWeight: 800, color: textPrimary, fontFamily: font, mb: 1.5 }}>
            {negatives.length} thing{negatives.length > 1 ? "s" : ""} need{negatives.length === 1 ? "s" : ""} you
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {negatives.map((s) => (
              <SituationRow key={s.situationId} situation={s} onAcknowledge={onAcknowledge} navigate={navigate} />
            ))}
          </Box>
        </>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.5, mb: hasPositives ? 1.5 : 0 }}>
          <CheckCircleIcon sx={{ color: success, fontSize: 26 }} />
          <Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: textPrimary, fontFamily: font }}>
              Nothing new — you're covered
            </Typography>
            {hasOngoing && (
              <Typography sx={{ fontSize: "12px", color: textMuted, fontFamily: font, mt: 0.2 }}>
                {ongoing.length} ongoing item{ongoing.length > 1 ? "s" : ""} below
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {hasPositives && (
        <Box sx={{ mt: hasNegatives ? 1.5 : 0 }}>
          {hasNegatives && (
            <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: textMuted, fontFamily: font, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.75 }}>
              Good news
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {positives.map((s) => (
              <SituationRow key={s.situationId} situation={s} onAcknowledge={onAcknowledge} navigate={navigate} />
            ))}
          </Box>
        </Box>
      )}

      {hasOngoing && (
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${border}` }}>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: textMuted, fontFamily: font, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.75 }}>
            Ongoing — already seen
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {ongoing.map((s) => (
              <SituationRow key={s.situationId} situation={s} onAcknowledge={onAcknowledge} navigate={navigate} dimmed />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
